import React, { useState, useRef, useEffect } from "react";
import {
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Avatar,
  Fade,
  Zoom,
  Divider,
  LinearProgress
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import PersonIcon from "@mui/icons-material/Person";

import axios from "axios";
import companyData from "../Data/companyData.js";
import Logo from "../logo.png";

// Professional SVG Icon
const AediaXIcon = () => {
  return (
    <svg
  width="60"
  height="60"
  viewBox="0 0 100 100"
  xmlns="http://www.w3.org/2000/svg"
  style={{
    filter: "drop-shadow(0 8px 25px rgba(120,0,255,0.5))"
  }}
>
  <defs>

    {/* Universe Background Gradient */}
    <radialGradient id="universeBg">
      <stop offset="0%" stopColor="#FFD700">
        <animate 
          attributeName="stop-color"
          values="#FFD700;#FF7A00;#FF00CC;#7B00FF;#FFD700"
          dur="8s"
          repeatCount="indefinite"
        />
      </stop>
      <stop offset="100%" stopColor="#2a004f">
        <animate 
          attributeName="stop-color"
          values="#2a004f;#3b0066;#240046;#2a004f"
          dur="8s"
          repeatCount="indefinite"
        />
      </stop>
    </radialGradient>

    {/* Glow */}
    <filter id="glow">
      <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
      <feMerge>
        <feMergeNode in="coloredBlur"/>
        <feMergeNode in="SourceGraphic"/>
      </feMerge>
    </filter>

  </defs>

  {/* Universe Background */}
  <circle
    cx="50"
    cy="50"
    r="45"
    fill="url(#universeBg)"
  >
    <animate
      attributeName="r"
      values="44;46;44"
      dur="6s"
      repeatCount="indefinite"
    />
  </circle>

  {/* Outer Magic Aura */}
  <circle
    cx="50"
    cy="50"
    r="38"
    stroke="rgba(255,255,255,0.4)"
    strokeWidth="1.5"
    fill="none"
  >
    <animateTransform
      attributeName="transform"
      type="rotate"
      from="0 50 50"
      to="360 50 50"
      dur="15s"
      repeatCount="indefinite"
    />
  </circle>

  {/* Magic Orb */}
  <circle
    cx="50"
    cy="50"
    r="16"
    fill="rgba(255, 252, 235, 0.9)"
    filter="url(#glow)"
  >
    <animate
      attributeName="r"
      values="15;18;15"
      dur="3s"
      repeatCount="indefinite"
    />
  </circle>

  {/* Rotating Magic Lines */}
  <g stroke="#ff5900" strokeWidth="2" strokeLinecap="round" opacity="0.7">
    <line x1="50" y1="15" x2="50" y2="25" />
    <line x1="85" y1="50" x2="75" y2="50" />
    <line x1="50" y1="85" x2="50" y2="75" />
    <line x1="15" y1="50" x2="25" y2="50" />

    <animateTransform
      attributeName="transform"
      type="rotate"
      from="0 50 50"
      to="360 50 50"
      dur="12s"
      repeatCount="indefinite"
    />
  </g>

  {/* Magic Star */}
  <polygon
    points="50,42 53,48 60,48 55,53 57,60 50,56 43,60 45,53 40,48 47,48"
    fill="#fcffa1"
    filter="url(#glow)"
  >
    <animate
      attributeName="opacity"
      values="0.6;1;0.6"
      dur="2s"
      repeatCount="indefinite"
    />
  </polygon>

</svg>
  );
};

const FloatingAIChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatCount, setChatCount] = useState(0);
  const MAX_CHATS_PER_DAY = 5;
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const API_KEY = "AIzaSyD7TXMo18E70LwoiMzkPkpSnhpMK1meYUw";

  const getUserId = () => {
    let userId = localStorage.getItem('aediax_user_id');
    if (!userId) {
      userId = 'user_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('aediax_user_id', userId);
    }
    return userId;
  };

  const loadUserSession = () => {
    const userId = getUserId();
    const today = new Date().toDateString();
    const savedSession = localStorage.getItem(`aediax_session_${userId}`);
    
    if (savedSession) {
      const session = JSON.parse(savedSession);
      if (session.date === today) {
        setChatCount(session.chatCount);
        return session.chatCount;
      } else {
        const newSession = { date: today, chatCount: 0 };
        localStorage.setItem(`aediax_session_${userId}`, JSON.stringify(newSession));
        setChatCount(0);
        return 0;
      }
    } else {
      const newSession = { date: today, chatCount: 0 };
      localStorage.setItem(`aediax_session_${userId}`, JSON.stringify(newSession));
      setChatCount(0);
      return 0;
    }
  };

  const updateUserSession = () => {
    const userId = getUserId();
    const today = new Date().toDateString();
    const newChatCount = chatCount + 1;
    const session = { date: today, chatCount: newChatCount };
    localStorage.setItem(`aediax_session_${userId}`, JSON.stringify(session));
    setChatCount(newChatCount);
    return newChatCount;
  };

  const hasRemainingChats = () => {
    return chatCount < MAX_CHATS_PER_DAY;
  };

  const getRemainingChats = () => {
    return MAX_CHATS_PER_DAY - chatCount;
  };

  useEffect(() => {
    loadUserSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [open]);

  const handleSend = async () => {
    if (!input.trim()) return;
    
    if (!hasRemainingChats()) {
      setMessages(prev => [
        ...prev,
        { 
          sender: "bot", 
          text: `You've reached the daily limit of ${MAX_CHATS_PER_DAY} conversations. Please contact us at ${companyData.contact.email} for assistance.`, 
          timestamp: new Date(),
          isError: true 
        }
      ]);
      setInput("");
      return;
    }

    const userMessage = { sender: "user", text: input, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const enhancedPrompt = `
        You are ${companyData.shortName} AI Assistant, a professional AI assistant for ${companyData.companyName}.
        
        IMPORTANT RULES:
        1. Always introduce yourself as "${companyData.shortName} AI Assistant"
        2. Provide SHORT, CONCISE answers (maximum 2-3 sentences)
        3. Use simple formatting with **bold** for emphasis only when necessary
        4. NO tables, NO code blocks, NO lists with bullet points
        5. Keep responses professional and helpful
        6. Answer based on this company information:
        
        Company: ${companyData.companyName}
        About: ${companyData.about}
        Services: ${companyData.services.join(", ")}
        Contact: Email ${companyData.contact.email}, Phone ${companyData.contact.phone}
        Location: ${companyData.registeredOffice.address}, ${companyData.registeredOffice.state}
        
        User Question: ${input}
        
        Provide a short, professional answer as ${companyData.shortName} AI Assistant.
      `;

      const response = await axios.post(
        `https://generativelanguage.googleapis.com/v1beta/models/gemini-flash-latest:generateContent?key=${API_KEY}`,
        {
          contents: [
            {
              parts: [
                {
                  text: enhancedPrompt
                }
              ]
            }
          ]
        }
      );

      let botText = response.data.candidates?.[0]?.content?.parts?.[0]?.text || 
        "Thank you for your question. Please contact us at info@aediax.com for more details.";

      botText = botText.replace(/```[\s\S]*?```/g, '');
      botText = botText.replace(/\|[\s\S]*?\|/g, '');
      botText = botText.trim();

      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: botText, timestamp: new Date() }
      ]);
      
      updateUserSession();
      
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        { 
          sender: "bot", 
          text: "Unable to connect. Please try again or contact us at info@aediax.com.", 
          timestamp: new Date(), 
          isError: true 
        }
      ]);
    }

    setLoading(false);
  };

  const formatTime = (date) => {
    return date?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatMessageText = (text) => {
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, index) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={index}>{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  return (
    <>
      {/* Floating Button */}
      <Zoom in={true} timeout={500}>
        <Fab
          onClick={() => setOpen(true)}
          sx={{
            position: "fixed",
            bottom: 24,
            right: 24,
            boxShadow: "0 4px 12px rgb(0, 0, 0)",
            transition: "all 0.2s",
            "&:hover": {
              transform: "scale(1.05)",
            }
          }}
        >
          <AediaXIcon />
        </Fab>
      </Zoom>

      {/* Chat Dialog */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Zoom}
        PaperProps={{
          sx: {
            width: "420px",
            height: "600px",
            maxWidth: "90vw",
            maxHeight: "85vh",
            borderRadius: 2,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.08)"
          }
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            bgcolor: "#ffffff",
            color: "#022f12",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #ffffff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Avatar
              src={Logo}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#f5f5f500"
              }}
            />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                {companyData.shortName} Assistant
              </Typography>
              <Typography variant="caption" sx={{ color: "#666", fontSize: "0.7rem"}}>
                {getRemainingChats()} of {MAX_CHATS_PER_DAY} remaining
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={() => setOpen(false)}
            sx={{ color: "#666", padding: "4px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>

        {/* Content */}
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            padding: "20px 24px",
            bgcolor: "#ffffff",
            overflow: "hidden",
            height: "100%"
          }}
        >
          {/* Usage Indicator */}
          <LinearProgress 
            variant="determinate" 
            value={(chatCount / MAX_CHATS_PER_DAY) * 100}
            sx={{
              height: 2,
              borderRadius: 1,
              mb: 2,
              bgcolor: "#f0f0f0",
              '& .MuiLinearProgress-bar': {
                bgcolor: "#000",
                borderRadius: 1
              }
            }}
          />

          {/* Messages */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              mb: 2,
              pr: 1,
              "&::-webkit-scrollbar": {
                width: "4px"
              },
              "&::-webkit-scrollbar-track": {
                bgcolor: "#f5f5f5",
                borderRadius: "4px"
              },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "#d0d0d0",
                borderRadius: "4px"
              }
            }}
          >
            {/* Welcome Message */}
            {messages.length === 0 && (
              <Fade in={true}>
                <Box sx={{ textAlign: "center", mt: 4 }}>
                  <Avatar
                    src={Logo}
                    sx={{
                      width: 80,
                      height: 80,
                      margin: "0 auto",
                      mb: 2,
                      bgcolor: "#f5f5f5"
                    }}
                  />
                  <Typography variant="h5" sx={{ fontSize: "1.25rem", fontWeight: 500, mb: 1 }}>
                    {companyData.companyName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#666", mb: 1, fontSize: "0.85rem" }}>
                    {companyData.tagline}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" sx={{ color: "#666", fontSize: "0.85rem" }}>
                    Ask me anything about our services, products, or company.
                  </Typography>
                </Box>
              </Fade>
            )}

            {/* Messages List */}
            {messages.map((msg, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: msg.sender === "user" ? "flex-end" : "flex-start",
                  alignItems: "flex-start",
                  gap: 1,
                  mb: 2
                }}
              >
                {msg.sender === "bot" && (
                  <Avatar
                    src={Logo}
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#f5f5f5"
                    }}
                  />
                )}
                
                <Box sx={{ maxWidth: "75%" }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: msg.sender === "user" ? "#000" : "#f5f5f5",
                      color: msg.sender === "user" ? "#fff" : "#000",
                      borderRadius: msg.sender === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px"
                    }}
                  >
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", fontSize: "0.85rem", lineHeight: 1.5 }}>
                      {formatMessageText(msg.text)}
                    </Typography>
                  </Paper>
                  {msg.timestamp && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        ml: 1,
                        color: "#999",
                        fontSize: "0.65rem"
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  )}
                </Box>

                {msg.sender === "user" && (
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: "#f5f5f5",
                      color: "#000"
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                )}
              </Box>
            ))}

            {/* Loading Indicator */}
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 1, mb: 2 }}>
                <Avatar src={Logo} sx={{ width: 32, height: 32, bgcolor: "#f5f5f5" }} />
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: "#f5f5f5",
                    borderRadius: "16px 16px 16px 4px"
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <CircularProgress size={14} thickness={5} sx={{ color: "#000" }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                      Processing...
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            )}
            
            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box sx={{ pt: 2, borderTop: "1px solid #f0f0f0" }}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <TextField
                inputRef={inputRef}
                fullWidth
                size="small"
                placeholder={hasRemainingChats() ? `Ask about ${companyData.shortName}...` : "Daily limit reached"}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && !loading && hasRemainingChats() && handleSend()}
                variant="outlined"
                disabled={loading || !hasRemainingChats()}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    bgcolor: "#ffffff",
                    fontSize: "0.85rem",
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000"
                    },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#000",
                      borderWidth: 1
                    }
                  }
                }}
              />
              <IconButton
                onClick={handleSend}
                disabled={loading || !input.trim() || !hasRemainingChats()}
                sx={{
                  bgcolor: "#000",
                  color: "#fff",
                  borderRadius: 1.5,
                  width: 40,
                  height: 40,
                  "&:hover": {
                    bgcolor: "#333"
                  },
                  "&:disabled": {
                    bgcolor: "#f0f0f0",
                    color: "#999"
                  }
                }}
              >
                <SendIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
            <Typography variant="caption" sx={{ display: "block", textAlign: "center", mt: 1.5, color: "#999", fontSize: "0.65rem" }}>
              {companyData.shortName} AI Assistant
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingAIChat;